/* eslint-disable no-underscore-dangle */
import axios, { AxiosError, AxiosResponse } from 'axios';
import { Cookies } from 'react-cookie';

import { PATHS } from 'src/navigation';
import config, { COOKIE_ACCESS_TOKEN_NAME } from '../config';
import API from './index';
import ROUTES from './routes';

const cookies = new Cookies();
let isRetrying = false;
let refreshSubscribers: ((newToken: string) => void)[] = [];
function subscribeTokenRefresh(cb: (newToken: string) => void): void {
  refreshSubscribers.push(cb);
}
function onRefreshed(newToken: string): void {
  refreshSubscribers.forEach((cb) => cb(newToken));
  refreshSubscribers = [];
}

const axiosInstance = axios.create({
  baseURL: config.API,
});
const requestParser = <T>(result: AxiosResponse<T>): T => result.data;
const errorHandler = <T>(error: AxiosError<T>): Promise<never> => Promise.reject(error?.response);
axiosInstance.interceptors.request.use((request) => request, errorHandler);

axiosInstance.interceptors.request.use(async (request) => {
  const token = cookies.get(COOKIE_ACCESS_TOKEN_NAME);

  if (token && request.headers) {
    request.headers.Authorization = `Bearer ${token}`;
  }

  return request;
});

axiosInstance.interceptors.response.use(
  (result) => {
    if (result.request?.responseURL?.includes('/users/me') && !result.data.payload.currentRole) {
      config.update({ TOKEN: '' });
    }
    return requestParser(result);
  },
  async (error) => {
    const { response } = error;
    const originalRequest = error.config;

    if (!response) {
      return Promise.reject(error);
    }

    if (response.status === 401 && !originalRequest._retry) {
      if (!response.request?.responseURL?.includes(ROUTES.AUTH_REFRESH)) {
        if (isRetrying) {
          return new Promise((resolve) => {
            subscribeTokenRefresh((newToken) => {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              resolve(axiosInstance(originalRequest));
            });
          });
        }

        originalRequest._retry = true;
        isRetrying = true;

        try {
          const {
            success,
            payload: { token },
          } = await API.refreshToken();

          if (success) {
            config.update({ TOKEN: token });
            onRefreshed(token);
            return axiosInstance(originalRequest);
          }
        } catch (e) {
          config.update({ TOKEN: '' });
          window.location.href = PATHS.LOGIN_PAGE;
        } finally {
          isRetrying = false;
        }
      }
    } else if (response.status === 403 && !response.config.url.includes('clearme')) {
      if (!config.TOKEN) {
        window.location.href = PATHS.LOGIN_PAGE;
      }
      window.location.href = PATHS.ERROR_403_PAGE;
    } else if (response.status === 404) {
      // window.location.href = PATHS.ERROR_404_PAGE;
    }

    return errorHandler(error);
  },
);

export default axiosInstance;
